import type { SwiperOptions, Swiper } from 'swiper/types';

export const useSwiper = () => {
    const mainSwiper = ref<Swiper>();

    const isBeginning = ref(false);
    const isEnd = ref(false);

    const setControls = () => {
        const swiper = unref(mainSwiper);
        if (swiper) {
            isBeginning.value = !swiper.params.loop && swiper.isBeginning;
            isEnd.value = !swiper.params.loop && swiper.isEnd;
        }
    };

    const swipeLeft = () => {
        if (mainSwiper.value) {
            mainSwiper.value.slidePrev();
        }
    };

    const swipeRight = () => {
        if (mainSwiper.value) {
            mainSwiper.value.slideNext();
        }
    };

    const onSlideFocus = (index: number) => {
        mainSwiper.value?.slideTo(index);
    };

    /**
     * initializes the swiper
     * @param swiper
     */
    const setMainSwiper = (swiper: Swiper) => {
        mainSwiper.value = swiper;
        if (mainSwiper.value) {
            mainSwiper.value.on('breakpoint', async () => {
                await wait();
                if (mainSwiper.value) {
                    setControls();
                }
            });
            setControls();
        }
    };

    const ssrIsActive = (isActive: boolean, index: number) => {
        if (!mainSwiper.value) return index === 0;
        return isActive;
    };
    let currentSlide = 0;
    const checkSwipe = () => {
        const activeIndex = mainSwiper.value?.realIndex;
        if (!activeIndex || activeIndex === currentSlide || mainSwiper.value?.autoplay?.running) return false;
        currentSlide = activeIndex;
        return true;
    };

    return {
        mainSwiper,
        isBeginning,
        isEnd,
        setControls,
        swipeLeft,
        swipeRight,
        onSlideFocus,
        setMainSwiper,
        ssrIsActive,
        checkSwipe,
    };
};

/**
 * Use to set swiper space between and grid gutter to avoid width jump before js loads
 * See also main.css
 * @param {number} slidesPerView default slides per view
 * @param {number} slidesPerViewMd slides per view on md breakpoint
 * @param {number} slidesPerViewLg slides per view on lg breakpoint
 * @param {number} spaceBetween gap between slides
 */
export const useSwiperPeak = (slidesPerView: number, slidesPerViewMd: number, slidesPerViewLg: number, spaceBetween: number) => {
    const md = screens('md');
    const lg = screens('lg');
    const breakpoints = {
        [md]: {
            slidesPerView: slidesPerViewMd,
        },
        [lg]: {
            slidesPerView: slidesPerViewLg,
        },
    };

    const sliderProps: Pick<SwiperOptions, 'breakpoints' | 'slidesPerView' | 'spaceBetween'> & { class: string } = {
        breakpoints,
        slidesPerView,
        spaceBetween,
        class: 'slider-peek !overflow-visible',
    };

    return {
        ...useSwiper(),
        componentProps: {
            style: {
                '--slides-per-view': slidesPerView,
                '--slides-per-view-md': slidesPerViewMd,
                '--slides-per-view-lg': slidesPerViewLg,
                '--space-between': spaceBetween,
            },
        },
        sliderProps,
    };
};
